import { Flex, Group } from '@mantine/core';
import { UseMutateFunction } from '@tanstack/react-query';

import type { OrganizationStatusesByProjectsAndBoards } from '../../../../../../api/organization-client/organization-client.type';
import type { Workflow } from '../../../../../../api/workflows-client/workflows-client.type';
import { useTranslation } from 'react-i18next';
import { baseWhite } from '../../../../../../styles/design-tokens';
import { Button } from '../../../../../../ui-library/button/button';
import { Icon } from '../../../../../../ui-library/icon/icon';
import { Text } from '../../../../../../ui-library/typography/typography';
import { EditableTextField } from '../../../../editable-text-field';
import { WorkflowAction } from '../../shared-components/reducer';

export function EditWorkflowHeader({
  state,
  dispatch,
  setPageState,
  isDirty,
  handleSelectWorkflow,
  statusesByProjectsAndBoards,
  workflows,
  updateWorkflow,
  openDeleteWorkflowModal,
}: {
  state: Workflow;
  dispatch: React.Dispatch<WorkflowAction>;
  setPageState: (pageState: 'update' | 'create' | 'idle') => void;
  isDirty: boolean;
  handleSelectWorkflow: (
    workflow: Workflow,
    statusesByProjectsAndBoards: OrganizationStatusesByProjectsAndBoards | undefined,
    dispatch: React.Dispatch<WorkflowAction>,
  ) => void;
  statusesByProjectsAndBoards: OrganizationStatusesByProjectsAndBoards | undefined;
  workflows: Workflow[];
  updateWorkflow: UseMutateFunction<
    Workflow,
    Error,
    {
      workflowId: string | null;
      workflow: Workflow;
    },
    unknown
  >;
  openDeleteWorkflowModal: () => void;
}) {
  const i18n = useTranslation();
  return (
    <Flex justify="space-between" align="center" w="100%" pb={16}>
      <EditableTextField
        value={state.name}
        canEdit
        isOpen
        onSave={(value: string) => {
          dispatch({
            type: 'UPDATE_FIELD',
            field: 'name',
            value,
          });
        }}
        onCancel={() => {}}
      />
      <Group>
        <Button
          variant="outline"
          size="xs"
          radius="xl"
          style={{ padding: '8px 16px', gap: '8px' }}
          onClick={() => {
            if (isDirty) {
              if (
                window.confirm(
                  "This page is asking you to confirm that you want to leave - information you've entered may not be saved.",
                )
              ) {
                const selectedWorkflow = workflows?.find((workflow) => workflow.id === state.id);
                dispatch({
                  type: 'SET_WORKFLOW',
                  payload: selectedWorkflow ?? state,
                });
                setPageState('idle');
                if (selectedWorkflow) {
                  handleSelectWorkflow(selectedWorkflow, statusesByProjectsAndBoards, dispatch);
                }
              }
            } else {
              const selectedWorkflow = workflows?.find((workflow) => workflow.id === state.id);
              dispatch({
                type: 'SET_WORKFLOW',
                payload: selectedWorkflow ?? state,
              });
              setPageState('idle');
              if (selectedWorkflow) {
                handleSelectWorkflow(selectedWorkflow, statusesByProjectsAndBoards, dispatch);
              }
            }
          }}
        >
          <Text>{i18n.t('common.actions.cancel')}</Text>
        </Button>
        <Button
          variant="warning"
          size="xs"
          radius="xl"
          leftSection={<Icon name="delete_outline" size={16} color={baseWhite} />}
          style={{ padding: '8px 16px', gap: '8px' }}
          onClick={openDeleteWorkflowModal}
        >
          <Text>{i18n.t('common.actions.delete')}</Text>
        </Button>
        <Button
          variant="primary"
          size="xs"
          radius="xl"
          leftSection={<Icon name="check_circle_outline" variant="filled" size={16} color="white" />}
          style={{ padding: '8px 16px', gap: '8px' }}
          onClick={() => {
            updateWorkflow({ workflowId: state.id, workflow: state });
          }}
        >
          <Text>{i18n.t('common.actions.save')}</Text>
        </Button>
      </Group>
    </Flex>
  );
}
